import CookiesMgr from 'coreClasses/cookies/CookiesMgr';
import {getConfig, isServer} from 'coreUtils/utils';
import {CMS_ORIGINS, CPA_CONNECTION_INFO_COOKIE, DEFAULT_CMS_URL, parseCpaConnectionInfo} from 'coreUtils/cms/contentHubConnection';
import type {TCpaConnectionInfo} from 'coreUtils/cms/contentHubConnection';

export interface LivePreviewProviderOptions {
	cmsUrl: string;
	cmsOrigin: string[];
	spaceId?: string;
	envId?: string;
}

/**
 * CPA connection override set from the DevToolkit for this browser session (values without the token), or
 * `null`. Browser only: the server reads the httpOnly cookie instead (`server/cms/contentHubConnection.ts`).
 * @returns {TCpaConnectionInfo | null}
 */
export const getCpaConnectionInfo = (): TCpaConnectionInfo | null =>
	isServer ? null : parseCpaConnectionInfo(CookiesMgr.getCookie(CPA_CONNECTION_INFO_COOKIE).getValue());

/**
 * Props for `LivePreviewProvider` of `@tlg-contenthub/live-preview`.
 *
 * Config overrides under `app.cmsAPI.parameters`:
 * - `cmsUrl`: CMS used for standalone "Edit" links;
 * - `livePreviewCmsOrigins`: extra CMS origins, e.g. a local CMS on a port other than 5173.
 *
 * In the browser the CPA connection override of the DevToolkit wins (CMS, space, environment).
 */
export const getLivePreviewProviderOptions = (): LivePreviewProviderOptions => {
	const params = getConfig()?.app?.cmsAPI?.parameters ?? {};
	const override = getCpaConnectionInfo();
	const cmsUrl: string = override?.cmsUrl || params.cmsUrl || DEFAULT_CMS_URL;
	const extraOrigins: string[] = params.livePreviewCmsOrigins ?? [];

	return {
		cmsUrl,
		cmsOrigin: [...new Set([new URL(cmsUrl).origin, ...CMS_ORIGINS, ...extraOrigins])],
		spaceId: override?.spaceId || params.spaceId,
		envId: override?.environmentId || params.environmentId,
	};
};

/** Id of the container that useFlatNavigation builds on the client: it is not a CMS entry. */
export const FLAT_SUB_LISTS_CONTAINER_ID = 'flatSubListsContainer';

const SYNTHETIC_ENTRY_IDS = new Set([FLAT_SUB_LISTS_CONTAINER_ID]);

/**
 * Whether the id belongs to an object built by the storefront rather than a CMS entry,
 * so it must not get an "Edit" button.
 * @param entryId Value of `sys.id`
 * @returns {boolean}
 */
export const isSyntheticEntryId = (entryId: string): boolean => SYNTHETIC_ENTRY_IDS.has(entryId);

/**
 * Whether the brand opted in to the "Edit" buttons in cookie-based CMS preview
 * (`cmsAPI.parameters.enableEntryInspector`). Inside the CMS iframe they are always on.
 * @returns {boolean}
 */
export const isEntryInspectorEnabled = (): boolean =>
	Boolean(getConfig()?.app?.cmsAPI?.parameters?.enableEntryInspector);
